import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, LogOut } from 'lucide-react';
import { useAuth } from './AuthContext';
import { fetchAllUsers } from './services/userService';
import { fetchGroupsWithMembers } from './services/groupService';
import {
  fetchAttendanceForWeek,
  upsertAttendanceBooking,
  bulkUpsertAttendanceBookings,
} from './services/attendanceService';

const OfficeAttendanceTool = () => {
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedUser, setSelectedUser] = useState('View All');
  const [alert, setAlert] = useState(null);
  const [allMembers, setAllMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // Store full user objects with IDs
  const [teams, setTeams] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [attendanceData, setAttendanceData] = useState({});

  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();

  // Helper to get display name from user object
  const getUserDisplayName = (user) => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || user.email;
  };

  // Current user from authenticated session
  const currentUser = userProfile ? getUserDisplayName(userProfile) : '';
  const isAdmin = userProfile?.is_admin || false;

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  // Load users and groups from database
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingData(true);
        setDataError(null);

        const [users, groupsWithMembers] = await Promise.all([
          fetchAllUsers(),
          fetchGroupsWithMembers(),
        ]);

        // Store full user objects
        setAllUsers(users);

        // Set all members (using full name as display name)
        setAllMembers(users.map(getUserDisplayName));

        // Transform groups data to match expected format
        const teamsObj = {};
        groupsWithMembers.forEach(group => {
          teamsObj[group.name] = group.GroupMember.map(gm => getUserDisplayName(gm.user));
        });
        setTeams(teamsObj);

        setLoadingData(false);
      } catch (error) {
        console.error('Error loading users and groups:', error);
        setDataError(error.message);
        setLoadingData(false);
      }
    };

    loadData();
  }, []);

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  // Helper to get user ID from display name
  const getUserIdByDisplayName = (displayName) => {
    const user = allUsers.find(u => getUserDisplayName(u) === displayName);
    return user?.id;
  };

  // Helper to convert date to YYYY-MM-DD format
  const getDateString = (dayIndex) => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1 + weekOffset * 7);
    const dayDate = new Date(startOfWeek);
    dayDate.setDate(startOfWeek.getDate() + dayIndex);
    const year = dayDate.getFullYear();
    const month = String(dayDate.getMonth() + 1).padStart(2, '0');
    const day = String(dayDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load attendance data from database
  useEffect(() => {
    const loadAttendance = async () => {
      if (allUsers.length === 0) return; // Wait for users to be loaded

      try {
        const bookings = await fetchAttendanceForWeek(weekOffset);

        // Transform bookings into a lookup structure: {userId: {date: status}}
        const attendanceMap = {};
        bookings.forEach(booking => {
          if (!attendanceMap[booking.user]) {
            attendanceMap[booking.user] = {};
          }
          attendanceMap[booking.user][booking.date] = booking.status;
        });

        setAttendanceData(attendanceMap);
      } catch (error) {
        console.error('Error loading attendance:', error);
      }
    };

    loadAttendance();
  }, [weekOffset, allUsers]);

  // Get attendance status for a member on a specific day
  const getAttendanceStatus = (member, dayIndex) => {
    const userId = getUserIdByDisplayName(member);
    if (!userId) return 'remote';

    const dateString = getDateString(dayIndex);
    return attendanceData[userId]?.[dateString] || 'remote';
  };

  // Show alert with auto-dismiss
  const showAlert = (message) => {
    setAlert(message);
    setTimeout(() => setAlert(null), 3000);
  };

  // Get week date range
  const getWeekRange = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1 + (weekOffset * 7));
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 4);
    
    return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  // Get date for specific day in Australian format (dd/mm)
  const getDayDate = (dayIndex) => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1 + (weekOffset * 7));
    
    const dayDate = new Date(startOfWeek);
    dayDate.setDate(startOfWeek.getDate() + dayIndex);
    
    return dayDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'numeric' });
  };

  // Check if selected user is a team
  const isTeamSelected = () => Object.keys(teams).includes(selectedUser);
  
  // Get members for highlighting
  const getHighlightedMembers = () => {
    if (selectedUser === 'View All') return [];
    if (isTeamSelected()) {
      return teams[selectedUser];
    }
    // Highlight the individual selected user
    return [selectedUser];
  };

  // Check if user can modify a specific member
  const canModifyMember = (member) => {
    // If "View All" is selected, no modifications allowed
    if (selectedUser === 'View All') return false;

    // Check if user is scheduling for a team
    if (isTeamSelected()) {
      return teams[selectedUser].includes(member);
    } else {
      // Check if user is scheduling for themselves
      return selectedUser === member;
    }
  };

  // Toggle attendance status
  const toggleAttendance = async (member, dayIndex) => {
    if (!canModifyMember(member)) return;

    const currentStatus = getAttendanceStatus(member, dayIndex);
    const newStatus = currentStatus === 'remote' ? 'office' : 'remote';

    if (isTeamSelected()) {
      await scheduleTeam(dayIndex, newStatus);
    } else {
      await scheduleIndividual(member, dayIndex, newStatus);
    }
  };

  // Schedule individual attendance
  const scheduleIndividual = async (member, dayIndex, status) => {
    const userId = getUserIdByDisplayName(member);
    if (!userId) return;

    const dateString = getDateString(dayIndex);

    // Optimistic update
    const newAttendanceData = { ...attendanceData };
    if (!newAttendanceData[userId]) {
      newAttendanceData[userId] = {};
    }
    newAttendanceData[userId][dateString] = status;
    setAttendanceData(newAttendanceData);

    try {
      await upsertAttendanceBooking(userId, dateString, status);
    } catch (error) {
      console.error('Error updating attendance:', error);
      showAlert('Failed to update attendance. Please try again later.');
      // Revert optimistic update by reloading
      const bookings = await fetchAttendanceForWeek(weekOffset);
      const attendanceMap = {};
      bookings.forEach(booking => {
        if (!attendanceMap[booking.user]) {
          attendanceMap[booking.user] = {};
        }
        attendanceMap[booking.user][booking.date] = booking.status;
      });
      setAttendanceData(attendanceMap);
    }
  };

  // Schedule team attendance
  const scheduleTeam = async (dayIndex, status) => {
    const teamMembers = teams[selectedUser];
    const dateString = getDateString(dayIndex);

    // Prepare bookings for all team members
    const bookings = teamMembers
      .map(member => {
        const userId = getUserIdByDisplayName(member);
        if (!userId) return null;
        return {
          user: userId,
          date: dateString,
          status: status,
        };
      })
      .filter(Boolean);

    if (bookings.length === 0) return;

    // Optimistic update
    const newAttendanceData = { ...attendanceData };
    bookings.forEach(booking => {
      if (!newAttendanceData[booking.user]) {
        newAttendanceData[booking.user] = {};
      }
      newAttendanceData[booking.user][booking.date] = booking.status;
    });
    setAttendanceData(newAttendanceData);

    try {
      await bulkUpsertAttendanceBookings(bookings);
    } catch (error) {
      console.error('Error updating team attendance:', error);
      showAlert('Failed to update team attendance. Please try again later.');
      // Revert optimistic update by reloading
      const bookingsData = await fetchAttendanceForWeek(weekOffset);
      const attendanceMap = {};
      bookingsData.forEach(booking => {
        if (!attendanceMap[booking.user]) {
          attendanceMap[booking.user] = {};
        }
        attendanceMap[booking.user][booking.date] = booking.status;
      });
      setAttendanceData(attendanceMap);
    }
  };

  // Get user dropdown options
  const getUserOptions = () => {
    const viewAllOption = [{ value: 'View All', label: 'View All (No Editing)' }];

    // Show only the logged-in user
    const currentUserDisplayName = allUsers
      .map(getUserDisplayName)
      .find(name => name === currentUser);

    const individuals = currentUserDisplayName
      ? [{ value: currentUserDisplayName, label: currentUserDisplayName }]
      : [];

    // Show only groups the logged-in user belongs to
    const userGroups = Object.keys(teams).filter(teamName =>
      teams[teamName].some(member => member === currentUser)
    );
    const teamOptions = userGroups.map(team => ({ value: team, label: `${team} (Group)` }));

    return [...viewAllOption, ...individuals, ...teamOptions];
  };

  // Show loading state
  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading attendance data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (dataError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{dataError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      {/* Alert */}
      {alert && (
        <div className="fixed top-4 right-4 bg-orange-100 border border-orange-400 text-orange-700 px-4 py-3 rounded z-50">
          {alert}
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Building className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">In Office Attendance</h1>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">Scheduling for:</span>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                >
                  {getUserOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Admin Panel"
                >
                  <Building className="w-4 h-4" />
                  <span>Admin Panel</span>
                </button>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Week Navigation - Front and Center */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center gap-4 mb-8">
          <button
            onClick={() => setWeekOffset(weekOffset - 1)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-lg hover:bg-gray-50 transition-colors"
          >
            ←
          </button>
          
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Week of {getWeekRange()}</h2>
            <p className="text-sm text-gray-600 mt-1">Select dates to manage attendance</p>
          </div>
          
          <button
            onClick={() => setWeekOffset(weekOffset + 1)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-lg hover:bg-gray-50 transition-colors"
          >
            →
          </button>
        </div>
        
        {/* Current Week Button - Only show if not on current week */}
        {weekOffset !== 0 && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => setWeekOffset(0)}
              className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
            >
              Jump to Current Week
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        {/* Office Attendance Grid */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-6">
            <h3 className="text-xl font-semibold mb-6">Weekly Attendance Overview</h3>
            
            {/* Attendance Grid */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-gray-900">Team Member</th>
                    {days.map((day, index) => (
                      <th key={day} className="text-center py-3 px-4 font-medium text-gray-900 min-w-24">
                        <div className="flex flex-col items-center">
                          <span className="text-xs text-gray-500 mb-1">{getDayDate(index)}</span>
                          <span>{day}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allMembers.map(member => {
                    const isHighlighted = getHighlightedMembers().includes(member);
                    
                    return (
                      <tr key={member} className="border-t border-gray-100">
                        <td className="py-4 px-4">
                          <div className={`flex items-center ${isHighlighted ? 'ring-2 ring-purple-300 rounded-lg p-2' : ''}`}>
                            <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
                              {member[0]}
                            </div>
                            <span className="font-medium text-gray-900">{member}</span>
                          </div>
                        </td>
                        {days.map((day, dayIndex) => {
                          const status = getAttendanceStatus(member, dayIndex);
                          const isOffice = status !== 'remote';
                          const canClick = canModifyMember(member);

                          return (
                            <td key={`${member}-${day}`} className="py-4 px-4 text-center">
                              <div
                                onClick={() => canClick && toggleAttendance(member, dayIndex)}
                                className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                                  isOffice
                                    ? 'bg-green-500 text-white'
                                    : 'bg-gray-100 text-gray-600'
                                } ${canClick ? 'cursor-pointer hover:opacity-80' : selectedUser === 'View All' ? 'cursor-default' : 'cursor-default opacity-50'}`}
                              >
                                {isOffice ? 'Office' : 'Remote'}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficeAttendanceTool;
